/**
 * Style files — the Order Book (build-plan Inc 1; cross-order layer step 2).
 *
 * KPI strip (open styles · pieces on order · ex-factory this week · drafts) +
 * status tabs + a toolbar (search · at-risk filter · group-by-stage toggle) +
 * a richly-styled table (buyer avatar · style code · type chip · ex-factory +
 * days-to-ship · pieces · bordered status badge · risk dot) with a visible-set
 * stats footer — mirroring the invoice list's grid language. Group-by-stage
 * turns the flat list into the "where is every order" stage board (cross-order
 * scope, rev 2). Risk dots reuse the deterministic `orderBook.listRisks` signal.
 *
 * @see apps/web/src/features/invoices/list/invoice-table.tsx — the grid we mirror
 * @see prototype/garment/order-book.html — the order-book / stage-board chrome
 * @see docs/product/garment-cross-style-boards-scope.md — the 2-link model
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { CalendarClock, FileStack, Layers, Package, Plus, Search } from "lucide-react";
import { Alert, Button, Combobox, TabsList, TabsRoot, TabsTrigger } from "@flint/ui";
import { trpc } from "../../../lib/trpc.js";
import { formatDate } from "../../../lib/format.js";
import { styleStatusLabel, styleStatusTone, type StyleStatus } from "../lib/status.js";
import { buildGarmentTypeGroups, garmentTypeLabel } from "../lib/garment-type.js";
import { dateRelative } from "../lib/relative-date.js";
import { avatarClassFor, initialsFor } from "../../master-list/lib/avatar-palette.ts";
import { DataTableCard, ListPagination } from "../../master-list/index.ts";
import type { StatusTone } from "@flint/ui";

type Tab = "all" | "draft" | "po_confirmed" | "in_production";

const TABS: Array<{ id: Tab; label: string }> = [
  { id: "all", label: "All" },
  { id: "draft", label: "Draft" },
  { id: "po_confirmed", label: "Confirmed" },
  { id: "in_production", label: "In production" },
];

/** Lifecycle order for the group-by-stage view. */
const STAGE_ORDER: StyleStatus[] = [
  "draft",
  "po_confirmed",
  "costing_locked",
  "pp_approved",
  "in_production",
  "shipped",
  "closed",
  "cancelled",
];

type RiskSeverity = "high" | "medium";

export function StylesListPage() {
  const [tab, setTab] = useState<Tab>("all");
  const [search, setSearch] = useState("");
  const [garmentTypeFilter, setGarmentTypeFilter] = useState("");
  const [grouped, setGrouped] = useState(false);
  const [atRiskOnly, setAtRiskOnly] = useState(false);
  const serverStatus = tab === "all" ? undefined : (tab as StyleStatus);

  const templates = trpc.styles.templates.useQuery();
  const garmentTypeGroups = useMemo(
    () => buildGarmentTypeGroups(templates.data ?? []),
    [templates.data],
  );

  // Cursor-stack pagination (the shared list pattern — see PO / vendors list).
  const PAGE_SIZE = 50;
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const cursor = cursorStack[cursorStack.length - 1];
  useEffect(() => {
    setCursorStack([]);
  }, [tab, garmentTypeFilter]);

  const list = trpc.styles.list.useQuery(
    {
      limit: PAGE_SIZE,
      ...(serverStatus ? { status: serverStatus } : {}),
      ...(garmentTypeFilter ? { garmentType: garmentTypeFilter } : {}),
      ...(cursor !== undefined ? { cursor } : {}),
    },
    { placeholderData: (prev) => prev },
  );

  // Deterministic risk signal per style — drives the row dot + the at-risk filter.
  const risksQuery = trpc.orderBook.listRisks.useQuery({});
  const riskByStyle = useMemo(() => {
    const m = new Map<string, { severity: RiskSeverity; reasons: string[] }>();
    for (const r of risksQuery.data?.risks ?? []) {
      m.set(r.styleId, { severity: r.severity, reasons: r.reasons.map((x) => x.label) });
    }
    return m;
  }, [risksQuery.data]);

  const rows = useMemo(() => list.data?.items ?? [], [list.data?.items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (atRiskOnly && !riskByStyle.has(r.id)) return false;
      if (!q) return true;
      return (
        r.styleRef.toLowerCase().includes(q) ||
        r.name.toLowerCase().includes(q) ||
        r.buyerName.toLowerCase().includes(q)
      );
    });
  }, [rows, search, atRiskOnly, riskByStyle]);

  const stats = useMemo(() => computeVisibleStats(filtered), [filtered]);

  const counts = list.data?.counts;
  const kpis = list.data?.kpis;
  const atRiskCount = riskByStyle.size;

  // Pagination derivations — page index = cursor-stack depth + 1. Computed
  // against the server-paged `rows` (before the client search / at-risk
  // narrowing), so the "Showing X–Y" caption reflects the fetched page.
  const pageNumber = cursorStack.length + 1;
  const showingFrom = rows.length === 0 ? 0 : cursorStack.length * PAGE_SIZE + 1;
  const showingTo = cursorStack.length * PAGE_SIZE + rows.length;
  const totalRows = counts ? (serverStatus ? counts[serverStatus] : counts.all) : undefined;
  const handlePrev = () => setCursorStack((s) => s.slice(0, -1));
  const handleNext = () => {
    const next = list.data?.nextCursor;
    if (next) setCursorStack((s) => [...s, next]);
  };

  const tabCount = (id: Tab): number | undefined => {
    if (!counts) return undefined;
    if (id === "all") return counts.all;
    return counts[id];
  };

  const renderRow = (row: (typeof rows)[number], serial: number) => {
    const risk = riskByStyle.get(row.id);
    const rel = exFactoryRelative(row.exFactoryDate);
    return (
      <tr key={row.id} className="border-t border-line transition-colors hover:bg-raised">
        <td className="px-4 py-3 align-top text-right">
          <span className="font-mono text-xs tabular-nums text-muted">{serial}</span>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <span
              aria-hidden
              title={risk ? risk.reasons.join(" · ") : undefined}
              className={`h-2 w-2 shrink-0 rounded-full ${
                risk ? (risk.severity === "high" ? "bg-bad" : "bg-warn") : "bg-transparent"
              }`}
            />
            <div className="min-w-0">
              <Link
                to="/merchandising/styles/$styleId/overview"
                params={{ styleId: row.id }}
                className="block truncate font-mono text-sm font-semibold text-ink hover:text-sky-700"
              >
                {row.styleRef}
              </Link>
              <div className="truncate text-xs text-muted">{row.name}</div>
            </div>
          </div>
        </td>
        <td className="px-3 py-3">
          <div className="flex min-w-0 items-center gap-2.5">
            <span
              aria-hidden
              className={`grid h-8 w-8 shrink-0 place-items-center rounded-md text-xs font-bold ${avatarClassFor(row.buyerName)}`}
            >
              {initialsFor(row.buyerName)}
            </span>
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-ink-2">{row.buyerName}</div>
              <div className="truncate text-xs text-muted">{buyerMeta(row)}</div>
            </div>
          </div>
        </td>
        <td className="px-3 py-3">
          <span className="inline-flex items-center rounded-full border border-line bg-bg px-2 py-0.5 text-xs text-muted">
            {garmentTypeLabel(row.garmentType)}
          </span>
        </td>
        <td className="px-3 py-3">
          <div className="font-mono text-sm text-ink-2">{row.exFactoryDate ? formatDate(row.exFactoryDate) : "—"}</div>
          {rel ? <div className={`text-xs ${rel.className}`}>{rel.label}</div> : null}
        </td>
        <td className="px-3 py-3 text-right">
          <div className="font-mono text-sm font-semibold text-ink">{row.totalQty.toLocaleString("en-IN")}</div>
          <div className="text-xs text-muted">
            {row.colourCount}c × {row.sizeCount}s
          </div>
        </td>
        <td className="px-3 py-3">
          <StyleStatusBadge status={row.status} />
        </td>
      </tr>
    );
  };

  // The table is identical across flat / grouped modes (the `<tbody>` branches
  // on `grouped`); only the surrounding chrome differs, so render it once.
  const tableEl = (
    <table className="w-full min-w-[900px] table-fixed text-sm">
      <colgroup>
        <col className="w-12" />
        <col />
        <col className="w-56" />
        <col className="w-36" />
        <col className="w-40" />
        <col className="w-28" />
        <col className="w-40" />
      </colgroup>
      <thead className="bg-raised text-xs uppercase tracking-wide text-muted">
        <tr>
          <th className="px-4 py-3 text-right font-medium">#</th>
          <th className="px-4 py-3 text-left font-medium">Style</th>
          <th className="px-3 py-3 text-left font-medium">Buyer</th>
          <th className="px-3 py-3 text-left font-medium">Type</th>
          <th className="px-3 py-3 text-left font-medium">Ex-factory</th>
          <th className="px-3 py-3 text-right font-medium">Pieces</th>
          <th className="px-3 py-3 text-left font-medium">Status</th>
        </tr>
      </thead>
      <tbody>
        {list.isLoading ? (
          <tr><td colSpan={7} className="px-3 py-8 text-center text-sm text-muted">Loading style files…</td></tr>
        ) : filtered.length === 0 ? (
          <tr>
            <td colSpan={7} className="px-3 py-10 text-center text-sm text-muted">
              {rows.length === 0 ? (
                <>
                  No style files yet.{" "}
                  <Link to="/merchandising/styles/new" className="text-sky-700 hover:underline">Open your first one.</Link>
                </>
              ) : (
                "No style files match the current filters."
              )}
            </td>
          </tr>
        ) : grouped ? (
          (() => {
            // Running 1-based serial that carries across stage groups.
            let serial = 0;
            return STAGE_ORDER.flatMap((st) => {
              const groupRows = filtered.filter((r) => r.status === st);
              if (groupRows.length === 0) return [];
              const pcs = groupRows.reduce((sum, r) => sum + r.totalQty, 0);
              return [
                <tr key={`h-${st}`} className="border-t border-line bg-bg">
                  <td colSpan={7} className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <StyleStatusBadge status={st} />
                      <span className="text-xs text-muted">· {groupRows.length}</span>
                      <span className="ml-auto font-mono text-xs text-muted">{pcs.toLocaleString("en-IN")} pcs</span>
                    </div>
                  </td>
                </tr>,
                ...groupRows.map((r) => renderRow(r, (serial += 1))),
              ];
            });
          })()
        ) : (
          // Flat mode: serial continues the server page offset (1-based).
          filtered.map((row, i) => renderRow(row, showingFrom + i))
        )}
      </tbody>
    </table>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-xl font-bold tracking-tight text-ink">Style files</h1>
          <p className="mt-1 text-sm text-muted">
            The Order Book — every buyer PO as a Style File, from draft to dispatch.
          </p>
        </div>
        <Button asChild>
          <Link to="/merchandising/styles/new">
            <Plus size={15} />
            New style file
          </Link>
        </Button>
      </div>

      {list.error ? (
        <Alert intent="bad" title="Couldn't load style files" description={list.error.message} />
      ) : null}

      <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi tone="sky" icon={<Layers size={16} />} label="Open styles" value={String(kpis?.openCount ?? 0)} meta="confirmed → in production" />
        <Kpi tone="ok" icon={<Package size={16} />} label="Pieces on order" value={(kpis?.openPieces ?? 0).toLocaleString("en-IN")} meta="across open styles" />
        <Kpi tone="warn" icon={<CalendarClock size={16} />} label="Ex-factory this week" value={String(kpis?.exFactoryThisWeekCount ?? 0)} meta="next 7 days" />
        <Kpi tone="muted" icon={<FileStack size={16} />} label="Drafts" value={String(kpis?.draftCount ?? 0)} meta="not yet confirmed" />
      </section>

      <TabsRoot value={tab} onValueChange={(v) => setTab(v as Tab)}>
        <TabsList aria-label="Style status">
          {TABS.map((t) => (
            <TabsTrigger key={t.id} value={t.id} count={tabCount(t.id)}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </TabsRoot>

      {/* Toolbar — search · at-risk filter · group-by-stage toggle */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search size={15} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search style, name, or buyer…"
            className="w-full rounded-md border border-line bg-paper py-1.5 pl-8 pr-3 text-sm text-ink placeholder:text-muted focus:border-sky-400 focus:outline-none"
          />
        </div>
        <div className="w-48">
          <Combobox
            value={garmentTypeFilter}
            onValueChange={(v) => setGarmentTypeFilter(v === garmentTypeFilter ? "" : v)}
            groups={garmentTypeGroups}
            placeholder="All types"
            searchPlaceholder="Filter by type…"
            emptyMessage="No matching type."
            className="h-8 text-xs"
          />
        </div>
        {garmentTypeFilter ? (
          <button
            type="button"
            onClick={() => setGarmentTypeFilter("")}
            className="text-xs text-muted underline hover:text-ink"
          >
            Clear
          </button>
        ) : null}
        <ToggleChip active={atRiskOnly} onClick={() => setAtRiskOnly((v) => !v)} disabled={atRiskCount === 0}>
          <span className={`h-2 w-2 rounded-full ${atRiskCount > 0 ? "bg-bad" : "bg-line-2"}`} aria-hidden />
          At-risk{atRiskCount > 0 ? ` (${atRiskCount})` : ""}
        </ToggleChip>
        <ToggleChip active={grouped} onClick={() => setGrouped((v) => !v)}>
          <Layers size={14} />
          Group by stage
        </ToggleChip>
      </div>

      {grouped ? (
        // Group-by-stage view keeps its own bordered card (stage-board layout)
        // — the shared DataTableCard pattern + pager only applies to the flat
        // table mode (pagination across collapsed stage groups is meaningless).
        <div className="overflow-hidden rounded-md border border-line bg-paper shadow-sm">
          <div className="overflow-x-auto">{tableEl}</div>
          {filtered.length > 0 ? (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-line bg-raised px-4 py-2.5 text-xs text-muted">
              <span>
                Visible <strong className="font-mono font-bold tabular-nums text-ink">{stats.count}</strong>
              </span>
              <span aria-hidden className="h-3 w-px bg-line-2" />
              <span>
                Total pieces <strong className="font-mono font-bold tabular-nums text-ink">{stats.totalPcs.toLocaleString("en-IN")}</strong>
              </span>
              <span aria-hidden className="h-3 w-px bg-line-2" />
              <span>
                Avg order <strong className="font-mono font-bold tabular-nums text-ink">{stats.avgPcs.toLocaleString("en-IN")}</strong>
              </span>
              {stats.largest ? (
                <>
                  <span aria-hidden className="h-3 w-px bg-line-2" />
                  <span>
                    Largest <strong className="font-mono font-bold tabular-nums text-ink">{stats.largest.qty.toLocaleString("en-IN")}</strong> · {stats.largest.styleRef}
                  </span>
                </>
              ) : null}
              {stats.dateRange ? (
                <>
                  <span aria-hidden className="h-3 w-px bg-line-2" />
                  <span>
                    Ex-factory <strong className="font-mono font-bold tabular-nums text-ink">{stats.dateRange}</strong>
                  </span>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : (
        <DataTableCard
          footer={
            filtered.length > 0
              ? [
                  { label: "Visible", value: stats.count },
                  { label: "Total pieces", value: stats.totalPcs.toLocaleString("en-IN") },
                  { label: "Avg order", value: stats.avgPcs.toLocaleString("en-IN") },
                  ...(stats.largest
                    ? [
                        {
                          label: "Largest",
                          value: stats.largest.qty.toLocaleString("en-IN"),
                          suffix: ` · ${stats.largest.styleRef}`,
                        },
                      ]
                    : []),
                  ...(stats.dateRange
                    ? [{ label: "Ex-factory", value: stats.dateRange }]
                    : []),
                ]
              : []
          }
          pagination={
            filtered.length > 0 ? (
              <ListPagination
                pageNumber={pageNumber}
                showingFrom={showingFrom}
                showingTo={showingTo}
                {...(typeof totalRows === "number" ? { total: totalRows } : {})}
                sortLabel="sorted by ex-factory date"
                hasPrev={cursorStack.length > 0}
                hasNext={!!list.data?.nextCursor}
                onPrev={handlePrev}
                onNext={handleNext}
              />
            ) : undefined
          }
        >
          {tableEl}
        </DataTableCard>
      )}
    </div>
  );
}

/** Visible-set aggregates for the footer stats bar (pure derivation from rows). */
function computeVisibleStats(rows: Array<{ styleRef: string; totalQty: number; exFactoryDate: string | null }>): {
  count: number;
  totalPcs: number;
  avgPcs: number;
  largest: { qty: number; styleRef: string } | null;
  dateRange: string | null;
} {
  if (rows.length === 0) return { count: 0, totalPcs: 0, avgPcs: 0, largest: null, dateRange: null };
  let total = 0;
  let largest: { qty: number; styleRef: string } | null = null;
  let minDate: string | null = null;
  let maxDate: string | null = null;
  for (const r of rows) {
    total += r.totalQty;
    if (largest === null || r.totalQty > largest.qty) largest = { qty: r.totalQty, styleRef: r.styleRef };
    if (r.exFactoryDate) {
      if (minDate === null || r.exFactoryDate < minDate) minDate = r.exFactoryDate;
      if (maxDate === null || r.exFactoryDate > maxDate) maxDate = r.exFactoryDate;
    }
  }
  const dateRange =
    minDate && maxDate ? (minDate === maxDate ? formatDate(minDate) : `${formatDate(minDate)} → ${formatDate(maxDate)}`) : null;
  return { count: rows.length, totalPcs: total, avgPcs: Math.round(total / rows.length), largest, dateRange };
}

/** Ex-factory relative cue — shared with the Orders list (lib/relative-date). */
const exFactoryRelative = dateRelative;

/** Secondary line under the buyer name — place (domestic) or country (export). */
function buyerMeta(row: { buyerCity: string | null; buyerState: string | null; buyerCountry: string | null }): string {
  const place = [row.buyerCity, row.buyerState].filter(Boolean).join(", ");
  if (place && row.buyerCountry) return `${place} · ${row.buyerCountry}`;
  return place || row.buyerCountry || "Location not set";
}

const BADGE_TONE: Record<StatusTone, string> = {
  sky: "border-sky-200 bg-sky-50 text-sky-700",
  ok: "border-emerald-200 bg-emerald-50 text-emerald-700",
  warn: "border-amber-200 bg-amber-50 text-amber-700",
  bad: "border-rose-200 bg-rose-50 text-rose-700",
  muted: "border-line bg-raised text-muted",
};

function StyleStatusBadge({ status }: { status: string }) {
  const tone = styleStatusTone(status as StyleStatus);
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-semibold ${BADGE_TONE[tone]}`}
    >
      <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current" />
      {styleStatusLabel(status as StyleStatus)}
    </span>
  );
}

function ToggleChip({
  active,
  disabled,
  onClick,
  children,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={[
        "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors",
        disabled
          ? "cursor-not-allowed border-line bg-paper text-muted opacity-60"
          : active
            ? "border-sky-300 bg-sky-50 text-sky-700"
            : "border-line bg-paper text-ink-2 hover:border-sky-300",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function Kpi({
  tone,
  icon,
  label,
  value,
  meta,
}: {
  tone: "sky" | "warn" | "ok" | "muted";
  icon: React.ReactNode;
  label: string;
  value: string;
  meta: string;
}) {
  const stripe = tone === "sky" ? "bg-sky-600" : tone === "warn" ? "bg-warn" : tone === "ok" ? "bg-ok" : "bg-line-2";
  const iconWrap =
    tone === "sky" ? "bg-sky-50 text-sky-700" : tone === "warn" ? "bg-warn-50 text-warn" : tone === "ok" ? "bg-ok-50 text-ok" : "bg-bg text-muted";
  return (
    <div className="relative overflow-hidden rounded-md border border-line bg-paper p-4 shadow-sm">
      <span className={`absolute inset-y-0 left-0 w-1 ${stripe}`} aria-hidden />
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted">{label}</span>
        <span className={`grid h-7 w-7 place-items-center rounded-md ${iconWrap}`}>{icon}</span>
      </div>
      <div className="mt-1.5 text-xl font-bold tracking-tight text-ink">{value}</div>
      <div className="mt-0.5 text-xs text-muted">{meta}</div>
    </div>
  );
}
